import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  createPortal,
} from 'react-dom';
import {
  PatientAuthorizationListItemDTOPaginatedResponseDTO,
} from 'dtos';
import DataProvider from 'core/context/data';
import VirtualTable from 'core/components/VirtualTable';
import type {
  Props,
} from 'pages/Dashboard/pages/Charts/pages/Details/types/tabContentProps';
import Button from 'pages/Dashboard/components/Button';
import AddIcon from '@mui/icons-material/Add';
import {
  authorizationQueryKeys,
  getPatientAuthorizations,
} from 'views/EMR/components/Authorizations/hooks';
import useAuthorizationDelete from 'views/EMR/components/Authorizations/deleteAction';
import AuthorizationDialog from 'views/EMR/components/Authorizations/Dialog';
import authorizationColumns, {
  AuthorizationTableMeta,
} from 'views/EMR/components/Authorizations/columns';
import {
  canExpandAuthorization,
  getAuthorizationUnitSubrows,
} from 'views/EMR/components/Authorizations/utils';
import type {
  AuthorizationRow,
} from 'views/EMR/components/Authorizations/utils';
import {
  SidePanelContext,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  ViewContext,
} from 'pages/Dashboard/contexts/viewContext';

type AuthorizationTableProps = {
  onOpenDetails: (authorizationId: number) => void;
  onEdit: (authorizationId: number) => void;
  onDelete: (authorizationId: number, authorizationNumber: string) => void;
  isDeletePending: boolean;
};

function AuthorizationTable({
  onOpenDetails,
  onEdit,
  onDelete,
  isDeletePending,
}: AuthorizationTableProps) {
  const tableMeta = useMemo<AuthorizationTableMeta>(
    () => ({
      onEdit,
      onDelete,
      isDeletePending,
    }),
    [isDeletePending, onDelete, onEdit],
  );

  // Use DataProvider for table data and server pagination.
  // Do not pass separate data to VirtualTable.
  return (
    <VirtualTable<AuthorizationRow>
      columns={authorizationColumns}
      meta={tableMeta}
      getRowId={(row, index) => row.authorizationUnitSubrow
        ? `${row.patientAuthorizationId}-${row.authorizationUnitSubrow.allowanceId}-${row.authorizationUnitSubrow.allowanceIndex}-${row.authorizationUnitSubrow.code}`
        : String(row.patientAuthorizationId ?? `skeleton-${index}`)}
      getSubRows={(row) => getAuthorizationUnitSubrows(row)}
      getRowCanExpand={(row) => canExpandAuthorization(row.original)}
      onRowClick={(_table, row) => {
        const authorizationId = row.original.patientAuthorizationId;
        onOpenDetails(authorizationId);
      }}
      defaultPagination={{
        pageIndex: 0,
        pageSize: 25,
      }}
      classes={{
        outerRow: '!w-full',
        subRow: '!border-t !border-gray-200 !pb-2',
        windowsClassName: '!w-full',
        scrollbarGutter: 'initial',
      }}
      skeleton={{ rows: 8 }}
      loadingSlot="Loading Authorizations..."
      enableRowSelection={false}
      hasFooter={false}
      emptySlot="No authorizations found"
    />
  );
}

export function AuthorizationOverview({ patient, footerSlotId }: Props) {
  const { isPanel } = useContext(ViewContext);
  const {
    openPanel,
    activePanel,
  } = useContext(SidePanelContext);
  const [editingAuthorizationId, setEditingAuthorizationId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [footerElement, setFooterElement] = useState<HTMLElement | null>(null);
  const {
    requestDelete,
    isPending: isDeletePending,
  } = useAuthorizationDelete({});

  useEffect(() => {
    setFooterElement(footerSlotId ? document.getElementById(footerSlotId) : null);
  }, [footerSlotId]);

  const openNew = useCallback(() => {
    setEditingAuthorizationId(null);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((authorizationId: number) => {
    setEditingAuthorizationId(authorizationId);
    setDialogOpen(true);
  }, []);

  const openDetails = useCallback((authorizationId: number) => {
    openPanel('authorization', { id: authorizationId, patient }, {
      canGoBack: isPanel,
      previousPanelInfo: isPanel && activePanel?.type === 'charges' ? {
        ...activePanel.panelInfo as object,
        tab: 'chart',
        chartTab: 'authorizations',
      } : undefined,
    });
  }, [activePanel, isPanel, openPanel, patient]);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingAuthorizationId(null);
  }, []);

  return (
    <>
      <div className="h-full min-h-0">
        <AuthorizationTable
          onOpenDetails={openDetails}
          onEdit={openEdit}
          onDelete={requestDelete}
          isDeletePending={isDeletePending}
        />
      </div>
      {footerElement && createPortal(
        <Button
          className="!h-8"
          variant="contained"
          color="primary"
          startIcon={<AddIcon className="!h-4 !w-4" />}
          onClick={openNew}
        >
          Add Authorization
        </Button>,
        footerElement,
      )}
      <AuthorizationDialog
        patient={patient}
        authorizationId={editingAuthorizationId}
        open={dialogOpen}
        onClose={closeDialog}
      />
    </>
  );
}

export default function Authorizations({ patient, footerSlotId }: Props) {
  const patientId = patient?.patientId ?? 0;

  return (
    <DataProvider<
      PatientAuthorizationListItemDTOPaginatedResponseDTO,
      AuthorizationRow
    >
      queryKey={authorizationQueryKeys.all[0]}
      params={{ patientId }}
      pagination={{ page: 0, pageIndex: 0, pageSize: 25 }}
      enabled={patientId > 0}
      getData={(params, options) => getPatientAuthorizations(patientId, params, options)}
      getQuery={({ pagination }) => ({
        page: pagination?.pageIndex ?? 0,
        pageSize: pagination?.pageSize ?? 25,
      })}
      getMeta={(response) => response.pageProperties ?? {}}
      normalize={(response) => response.data ?? []}
    >
      <AuthorizationOverview patient={patient} section="authorizations" footerSlotId={footerSlotId} />
    </DataProvider>
  );
}
